import React, { useContext, useEffect, useState } from 'react'
import { Pressable, StyleSheet, View, Linking } from 'react-native'
import Icon from 'react-native-vector-icons/MaterialIcons'
import { Text } from 'react-native-elements'

import Colors from '../constants/Colors'
import { getPDFUri } from '../screens/PDFReaderScreen'
import { IAnalysisSource } from '../api/analyses'
import { DropdownList } from './DropdownList'
import { NavbarIconButton, navbarIcons } from './NavbarIconButton'

import {
  NativeStackHeaderProps,
  NativeStackNavigationProp,
} from '@react-navigation/native-stack'
import {
  BottomTabHeaderProps,
  BottomTabNavigationProp,
} from '@react-navigation/bottom-tabs'
import { ParamListBase, Route, RouteProp } from '@react-navigation/native'
import { analysisDisabled } from '../constants/Config'
import UnreadBadge from './UnreadBadge'
import ConfirmationModalModal from './ConfirmationModal'
import { UserContext } from '../context/user.context'

const titles: Record<string, string> = {
  Root: '',
  Favorites: 'Kedvencek',
  Search: '',
  AnalysisRoot: 'Elemzések',
  AnalysisSearch: 'Keresés',
  AnalysisFavorites: 'Kedvencek',
}

const modalSettings = {
  text: 'Ez a művelet az összes cikket olvasottként fogja megjelölni.\nA változtatás nem vonható vissza.\nBiztosan folytatja?',
  submitText: 'Igen',
  title: 'Minden cikket olvasottként megjelöl?',
} as const

const setActiveState = (
  routeName: string,
  setActive: React.Dispatch<React.SetStateAction<string>>
) => {
  if (
    routeName === 'AnalysisRoot' ||
    routeName === 'AnalysisSearch' ||
    routeName === 'AnalysisFavorites'
  ) {
    setActive('Analysis')
  } else if (
    routeName === 'Root' ||
    routeName === 'Search' ||
    routeName === 'Favorites'
  ) {
    setActive('Home')
  } else {
    setActive(routeName)
  }
}

const setIsPDFScreenState = (
  routeName: string,
  setIsPDFScreen: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (routeName === 'PDFReader') {
    setIsPDFScreen(true)
  } else {
    setIsPDFScreen(false)
  }
}

const setIsDropdownVisibleState = (
  routeName: string,
  setIsDropdownVisible: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (routeName === 'Root' || routeName === 'Search') {
    setIsDropdownVisible(true)
  } else {
    setIsDropdownVisible(false)
  }
}

const setIsCanGoBackState = (
  routeName: string,
  setIsCanGoBack: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (routeName in titles) {
    setIsCanGoBack(false)
  } else {
    setIsCanGoBack(true)
  }
}

const iconHeaderSide = (
  navigation:
    | BottomTabNavigationProp<ParamListBase, string>
    | NativeStackNavigationProp<ParamListBase, string>,
  active: string,
  side: keyof typeof navbarIcons
) => (
  <View style={styles.wideSide}>
    <NavbarIconButton navigation={navigation} active={active} side={side} />
  </View>
)

const leftHeaderSideCanGoBack = (
  navigation:
    | BottomTabNavigationProp<ParamListBase, string>
    | NativeStackNavigationProp<ParamListBase, string>
) => (
  <Pressable
    onPress={() => {
      navigation.goBack()
    }}
    style={styles.narrowSide}
  >
    <Icon
      name={'arrow-back-ios'}
      size={18}
      color={Colors.orangeWeb}
      style={styles.icon}
    />
  </Pressable>
)

const rightHeaderSideCanGoBack = (
  route: Route<string, object | undefined> | RouteProp<ParamListBase, string>,
  isPDFScreen: boolean
) =>
  isPDFScreen ? (
    <Pressable
      onPress={() => {
        Linking.openURL(getPDFUri((route?.params as IAnalysisSource).Uri))
      }}
      style={styles.narrowSide}
    >
      <Icon
        name={'save'}
        size={26}
        color={Colors.orangeWeb}
        style={styles.icon}
      />
    </Pressable>
  ) : (
    <View style={styles.narrowSide}></View>
  )

const leftHomeButton = (
  navigation:
    | BottomTabNavigationProp<ParamListBase, string>
    | NativeStackNavigationProp<ParamListBase, string>,
  active: string,
  handleOpenModal: (value: boolean) => void
) => {
  if (!analysisDisabled) {
    return iconHeaderSide(navigation, active, 'left')
  }

  return <UnreadBadge handleOpen={handleOpenModal} />
}

export const HeaderComponent: React.FC<
  NativeStackHeaderProps | BottomTabHeaderProps
> = (props) => {
  const { navigation, route, options } = props
  if (!navigation.canGoBack) return null
  const SourceName = (route?.params as ParamListBase)?.SourceName
  const [active, setActive] = useState('')
  const [isCanGoBack, setIsCanGoBack] = useState(false)
  const [isPDFScreen, setIsPDFScreen] = useState(false)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { setMarkAllRead } = useContext(UserContext)

  const handleOpenModal = (value: boolean) => {
    setIsOpen(value)
  }

  useEffect(() => {
    setActiveState(route.name, setActive)
    setIsPDFScreenState(route.name, setIsPDFScreen)
    setIsDropdownVisibleState(route.name, setIsDropdownVisible)
    setIsCanGoBackState(route.name, setIsCanGoBack)
  }, [route])

  return (
    <>
      <View style={styles.screenOptionsHeader}>
        {isCanGoBack
          ? leftHeaderSideCanGoBack(navigation)
          : leftHomeButton(navigation, active, handleOpenModal)}
        <View style={styles.view}>
          <Text style={styles.screenOptionsHeaderTitle}>
            {isDropdownVisible ? (
              <DropdownList navigation={navigation} />
            ) : (
              titles[route.name] || SourceName || options.headerTitle || ''
            )}
          </Text>
        </View>
        {isCanGoBack
          ? rightHeaderSideCanGoBack(route, isPDFScreen)
          : iconHeaderSide(navigation, active, 'right')}
      </View>
      <ConfirmationModalModal
        isVisible={isOpen}
        setVisible={setIsOpen}
        onSubmit={() => setMarkAllRead()}
        text={modalSettings.text}
        submitText={modalSettings.submitText}
        title={modalSettings.title}
      />
    </>
  )
}

const styles = StyleSheet.create({
  screenOptionsHeader: {
    height: 50,
    minHeight: 50,
    maxHeight: 50,
    backgroundColor: Colors.oxfordBlue,
    alignItems: 'center',
    flexDirection: 'row',
  },
  screenOptionsHeaderTitle: {
    color: Colors.orangeWeb,
    fontFamily: 'Roboto_500Medium',
    fontSize: 18,
    textAlign: 'center',
  },
  view: { flex: 1 },
  icon: {
    marginLeft: 13,
    marginRight: 6,
  },
  narrowSide: { minWidth: 50 },
  wideSide: { minWidth: 80 },
})
